import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import '../../styles/design-tokens.css';
import './Navbar.css';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Courses', to: '/all-courses' },
  // { label: 'Study Abroad', to: '/study-abroad' },
  // { label: 'Lingual Services', to: '/lingual-services' },
  { label: 'Feedback', to: '/feedback' },
  { label: 'Career', to: '/career' },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const { isAuthenticated, currentUser, logout } = useAuthStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!profileOpen) return undefined;

    const closeOnOutsideClick = event => {
      if (!profileRef.current?.contains(event.target)) {
        setProfileOpen(false);
      }
    };
    const closeOnEscape = event => {
      if (event.key === 'Escape') setProfileOpen(false);
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [profileOpen]);

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    setMenuOpen(false);
    navigate('/');
  };

  const isActive = to => {
    if (to === '/') return pathname === '/';
    if (to === '/all-courses') return pathname.startsWith(to) || pathname === '/dashboard';
    return pathname.startsWith(to);
  };
  const coursesDestination = isAuthenticated ? '/dashboard' : '/all-courses';

  return (
    <header className="sa-header">
      <nav className="sa-navbar" aria-label="Main navigation">
        <div className="sa-container">
          <Link to="/" className="sa-logo" aria-label="Somayya Academy home">
            <img
              src="/images/Somayya-Academy-Logo-for-Website-Home.001.png"
              alt="Somayya Academy"
              onError={event => {
                event.currentTarget.src = '/images/Somayya_academy_transparent.png';
              }}
            />
          </Link>

          <ul className="sa-nav-links">
            {NAV_LINKS.map(link => (
              <li key={link.to} className={isActive(link.to) ? 'sa-active' : ''}>
                <Link to={link.label === 'Courses' ? coursesDestination : link.to}>
                  {link.label}
                </Link>
              </li>
            ))}
            {isAuthenticated ? (
              <li
                ref={profileRef}
                className={`sa-profile-nav-item${profileOpen ? ' is-open' : ''}`}
              >
                <button
                  className={`sa-profile-toggle${pathname.startsWith('/profile') ? ' is-active' : ''}`}
                  type="button"
                  onClick={() => setProfileOpen(open => !open)}
                  aria-expanded={profileOpen}
                  aria-haspopup="true"
                >
                  Profile <span aria-hidden="true">▾</span>
                </button>
                {profileOpen && (
                  <div className="sa-profile-menu">
                    <span className="sa-profile-email">
                      {currentUser?.email || 'Signed-in user'}
                    </span>
                    <button type="button" onClick={handleLogout}>Log Out</button>
                  </div>
                )}
              </li>
            ) : (
              <li>
                <Link to="/login">Sign In / Login</Link>
              </li>
            )}
          </ul>

          <button
            className="sa-hamburger"
            type="button"
            onClick={() => setMenuOpen(open => !open)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      <div className={`sa-mobile-menu${menuOpen ? ' open' : ''}`}>
        <ul>
          {NAV_LINKS.map(link => (
            <li key={link.to} className={isActive(link.to) ? 'sa-active' : ''}>
              <Link
                to={link.label === 'Courses' ? coursesDestination : link.to}
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </Link>
            </li>
          ))}
          {isAuthenticated ? (
            <>
              <li className="sa-mobile-profile-email">
                {currentUser?.email || 'Signed-in user'}
              </li>
              <li>
                <button className="sa-mobile-menu-action" type="button" onClick={handleLogout}>
                  Log Out
                </button>
              </li>
            </>
          ) : (
            <li>
              <Link to="/login" onClick={() => setMenuOpen(false)}>Sign In / Login</Link>
            </li>
          )}
        </ul>
      </div>
    </header>
  );
}
